import { Div, Base, createEmitter } from '@codesuma/baseline'
import list, { Contact, ListMember } from '../../services/list'
import helpers from '../../helpers'
import { toast } from '../../utils/toast'
import { haptic } from '../../utils/haptics'
import style from './index.module.css'

export const SharePanel = () => {
    const emitter = createEmitter()

    const overlay = Div()
    overlay.addClass(style.overlay)

    const panel = Div()
    panel.addClass(style.panel)

    // Header
    const header = Div()
    header.addClass(style.header)

    const title = Div('Share List')
    title.addClass(style.title)

    const closeBtn = Div('\u00d7')
    closeBtn.addClass(style.closeBtn)

    header.append(title, closeBtn)

    // One input shares both ways: a sharing ID (works for anonymous users) or
    // an email address. Which path to take is detected from what's typed —
    // an "@" means email (see handleShare).
    const handleRow = Div()
    handleRow.addClass(style.addRow)

    const handleInput = Base('input') as any
    handleInput.addClass(style.emailInput)
    handleInput.el.setAttribute('placeholder', 'Their sharing ID or email…')
    handleInput.el.setAttribute('type', 'text')
    // Sharing IDs are lowercase codes, not sentences — stop the phone keyboard
    // from capitalizing or "correcting" them (the server matches forgivingly
    // anyway, but don't invite the mistake). Emails don't mind either.
    handleInput.el.setAttribute('autocapitalize', 'none')
    handleInput.el.setAttribute('autocorrect', 'off')
    handleInput.el.setAttribute('spellcheck', 'false')

    const shareBtn = Div('Share')
    shareBtn.addClass(style.inviteBtn)

    handleRow.append(handleInput, shareBtn)

    // People already on one of the user's lists. Shown the moment the input is
    // focused, so sharing with someone you've shared with before is a tap
    // instead of reading a code off another list and typing it back in.
    const suggestions = Div()
    suggestions.addClass(style.suggestions)
    suggestions.style({ display: 'none' })

    // Error display
    const errorEl = Div()
    errorEl.addClass(style.error)
    errorEl.style({ display: 'none' })

    // Members list
    const membersContainer = Div()
    membersContainer.addClass(style.members)

    const loadingEl = Div('Loading members...')
    loadingEl.addClass(style.loading)

    const emptyEl = Div('No members yet — invite someone!')
    emptyEl.addClass(style.emptyMembers)

    panel.append(header, handleRow, suggestions, errorEl, membersContainer)
    overlay.append(panel)

    // Show error message
    const showError = (msg: string) => {
        errorEl.text(msg)
        errorEl.style({ display: 'block' })
        setTimeout(() => errorEl.style({ display: 'none' }), 4000)
    }

    // Long-press a member to copy what you'd type to share with them again — the
    // sharing ID, or their email if that's all we have. Saves reading a code off
    // one list to type it into another.
    const copyOnHold = (row: ReturnType<typeof Div>, value: string) => {
        let timer: ReturnType<typeof setTimeout> | undefined
        let x = 0
        let y = 0
        const cancel = () => clearTimeout(timer)
        row.el.addEventListener('pointerdown', (e: PointerEvent) => {
            x = e.clientX
            y = e.clientY
            timer = setTimeout(async () => {
                haptic()
                try {
                    await navigator.clipboard.writeText(value)
                    toast('Copied ' + value)
                } catch (err) {
                    console.error('Failed to copy the sharing ID:', err)
                    toast("Couldn't copy")
                }
            }, 550)
        })
        row.el.addEventListener('pointermove', (e: PointerEvent) => {
            if (Math.abs(e.clientX - x) > 8 || Math.abs(e.clientY - y) > 8) cancel()
        })
        row.el.addEventListener('pointerup', cancel)
        row.el.addEventListener('pointercancel', cancel)
        row.el.addEventListener('contextmenu', e => e.preventDefault())
    }

    // Render members
    const renderMembers = (members: ListMember[]) => {
        membersContainer.empty()

        if (members.length === 0) {
            membersContainer.append(emptyEl)
            return
        }

        members.forEach(member => {
            const row = Div()
            row.addClass(style.memberRow)

            const info = Div()
            info.addClass(style.memberInfo)

            const email = Div(member.display_name || member.handle || member.email || 'Member')
            email.addClass(style.memberEmail)

            const role = Div(member.status === 'pending' ? 'pending' : member.role)
            role.addClass(style.memberRole)

            info.append(email, role)
            row.append(info)

            // Nothing to paste elsewhere for a member we only know by row id.
            const shareValue = member.handle || member.email
            if (shareValue) copyOnHold(row, shareValue)

            if (member.role !== 'owner') {
                const removeBtn = Div('\u00d7')
                removeBtn.addClass(style.removeBtn)
                removeBtn.el.onclick = async () => {
                    try {
                        await list.removeMember(member.id)
                        loadMembers()
                    } catch (err) {
                        showError('Failed to remove member')
                    }
                }
                row.append(removeBtn)
            }

            membersContainer.append(row)
        })

        // The hold gesture is invisible otherwise — say it out loud. Built fresh
        // each render: baseline's empty() strips a reused instance for good.
        if (members.some(m => m.handle || m.email)) {
            const hint = Div('Hold a name to copy their sharing ID')
            hint.addClass(style.copyHint)
            membersContainer.append(hint)
        }
    }

    // Load members from server
    const loadMembers = async () => {
        membersContainer.empty()
        membersContainer.append(loadingEl)
        const members = await list.getMembers()
        onThisList = members
        renderMembers(members)
        renderSuggestions()
    }

    // --- Share suggestions ---
    let contacts: Contact[] = []
    let onThisList: ListMember[] = []
    let picking = false // suggestions only appear once the input is in play

    // What the user would type to reach this person: their sharing ID, or their
    // email for an invite that hasn't been accepted yet (no handle to show).
    const contactValue = (c: Contact) => c.handle || c.email || ''
    const contactLabel = (c: Contact) => c.display_name || c.handle || c.email || 'Someone'

    const alreadyHere = (c: Contact) =>
        onThisList.some(m => (c.handle && m.handle === c.handle) || (c.email && m.email === c.email))

    const renderSuggestions = () => {
        suggestions.empty()
        const typed = (handleInput.el as HTMLInputElement).value.trim().toLowerCase()
        const matches = contacts
            .filter(c => contactValue(c) && !alreadyHere(c))
            .filter(c => !typed || contactLabel(c).toLowerCase().includes(typed) || contactValue(c).toLowerCase().includes(typed))

        if (!picking || !matches.length) return suggestions.style({ display: 'none' })

        const hint = Div('People you already share with')
        hint.addClass(style.suggestHint)
        suggestions.append(hint)

        matches.forEach(c => {
            const row = Div()
            row.addClass(style.suggestion)
            const name = Div(contactLabel(c))
            name.addClass(style.suggestName)
            row.append(name)
            // The label is often just the sharing ID — only add the second line
            // when it actually says something the first one doesn't.
            if (contactLabel(c) !== contactValue(c)) {
                const sub = Div(contactValue(c))
                sub.addClass(style.suggestSub)
                row.append(sub)
            }
            // pointerdown, not click: the input's blur would otherwise land first.
            row.el.addEventListener('pointerdown', e => {
                e.preventDefault() // keep focus off the row so the input keeps it
                ;(handleInput.el as HTMLInputElement).value = contactValue(c)
                picking = false
                renderSuggestions()
            })
            suggestions.append(row)
        })
        suggestions.style({ display: 'block' })
    }

    handleInput.el.addEventListener('focus', () => { picking = true; renderSuggestions() })
    handleInput.el.addEventListener('input', renderSuggestions)

    // Pull the server's friendly message out of "HTTP 4xx: {"error":"…"}".
    const serverError = (err: any, fallback: string): string => {
        const m = String(err?.message || '')
        const match = m.match(/\{.*\}$/)
        if (match) {
            try { return JSON.parse(match[0]).error || fallback } catch { /* ignore */ }
        }
        return fallback
    }

    const handleShare = async () => {
        const value = (handleInput.el as HTMLInputElement).value.trim()
        if (!value) return
        const btn = shareBtn.el as HTMLElement
        try {
            btn.style.opacity = '0.5'
            btn.style.pointerEvents = 'none'
            // "@" = email invite; anything else is treated as a sharing ID.
            if (value.includes('@')) await list.addMember(value)
            else await list.shareByHandle(value)
            ;(handleInput.el as HTMLInputElement).value = ''
            picking = false
            loadMembers()
        } catch (err: any) {
            showError(serverError(err, 'Failed to share'))
        } finally {
            btn.style.opacity = '1'
            btn.style.pointerEvents = 'auto'
        }
    }
    shareBtn.el.onclick = handleShare
    handleInput.el.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') handleShare()
    })

    // Back = close: show() pushes a state-only history marker (same URL — the
    // router ignores same-path pops), so the hardware/browser back button pops
    // the marker and closes the sheet instead of leaving the page. The X and
    // backdrop also close through history.back(), so the marker never leaks a
    // dead extra back-press.
    let markerLive = false
    const onPop = () => {
        window.removeEventListener('popstate', onPop)
        markerLive = false
        emitter.emit('close')
    }
    const requestClose = () => {
        if (markerLive) {
            markerLive = false
            history.back() // onPop delivers the actual 'close'
        } else {
            emitter.emit('close')
        }
    }

    closeBtn.el.onclick = requestClose
    overlay.el.onclick = (e: MouseEvent) => {
        if (e.target === overlay.el) requestClose()
    }

    // Public API
    const show = () => {
        // Match the color of the list page that opened us — solid list color
        // background, the themed text color, and --accent for the colored buttons
        // (the dark hue on light, the vibrant color on night).
        const id = list.getListId() || ''
        panel.style({ backgroundColor: helpers.colorForId(id), color: helpers.textForId(id) })
        panel.el.style.setProperty('--accent', helpers.accentForId(id))
        if (!markerLive) {
            history.pushState({ sharePanel: true }, '')
            markerLive = true
            window.addEventListener('popstate', onPop)
        }
        picking = false
        suggestions.style({ display: 'none' })
        loadMembers()
        // Fire-and-forget: the panel is usable without it, and it only matters
        // once the input is focused.
        list.getContacts().then(c => { contacts = c; renderSuggestions() })
    }

    return Object.assign(overlay, {
        on: emitter.on.bind(emitter),
        show,
    })
}
